import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Sparkles, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

type Props = {
  trigger: React.ReactNode;
  title: string;
  description?: string;
  /** Async function that returns the generated text. */
  generate: () => Promise<string>;
  /** Optional extras rendered above the action row (e.g. tone/language selects). */
  controls?: React.ReactNode;
  /** Auto-run on open. */
  autoRun?: boolean;
};

export function AiTextDialog({
  trigger,
  title,
  description,
  generate,
  controls,
  autoRun,
}: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    setText("");
    try {
      const t = await generate();
      setText(t);
    } catch (e: any) {
      setErr(e?.message ?? "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        setOpen(v);
        if (v && autoRun && !text) {
          void run();
        }
        if (!v) {
          setText("");
          setErr(null);
        }
      }}
    >
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-gold" /> {title}
          </DialogTitle>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </DialogHeader>

        {controls && <div className="space-y-2">{controls}</div>}

        <div className="bg-muted/30 max-h-[55vh] min-h-[260px] overflow-y-auto whitespace-pre-wrap rounded-lg border border-border p-4 text-sm leading-relaxed">
          {busy && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Generating with AI…
            </div>
          )}
          {!busy && err && <div className="text-destructive">{err}</div>}
          {!busy &&
            !err &&
            (text || (
              <span className="text-muted-foreground">
                Click “Generate” to draft with AI.
              </span>
            ))}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (text) {
                navigator.clipboard.writeText(text);
                toast.success("Copied");
              }
            }}
            disabled={!text || busy}
          >
            <Copy className="mr-2 h-4 w-4" /> Copy
          </Button>
          <Button onClick={run} disabled={busy}>
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            {text ? "Regenerate" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
